import React, { useEffect, useReducer, useMemo, useCallback } from 'react';
import FiltrosGenerico from '../UI/FiltrosGenerico.js';
import CampoEditableTexto from '../UI/campos/CampoEditableTexto.js';
import ItemLista from '../UI/ItemLista.js';
import SelectPais from '../selectores/SelectPais.js';
import SelectProvincia from '../selectores/SelectProvincia.js';
import SelectLocalidad from '../selectores/SelectLocalidad.js';
import useSearchURL from '../../hooks/useSearchURL.js';
import * as Filtros from '../constantes/Filtros.js';
import * as FiltrosURL from '../constantes/FiltrosURL.js';
import * as Funciones from '../funciones/FuncionesFiltros.js';

const reducerFiltros = (state, action) => {
  return Funciones.ReducerFiltros(Filtros.PERSONAS, state, action);
};

const FiltrosPersonas = (props) => {
  const searchParameters = props.searchParams;
  const profesion = props.profesion;
  const [filtros, setFiltros] = useReducer(reducerFiltros, null);
  const { isBuscando, erroresSearch, searchData, resetErroresSearch } = useSearchURL();

  // Función para generar el título dinámico según la profesión
  const getTituloFiltro = () => {
    if (!profesion) {
      return 'Buscar Personas';
    }
    if (profesion === Filtros.PathPersona.JUGADORES) {
      return 'Buscar Jugadores';
    }
    if (profesion === Filtros.PathPersona.TECNICOS) {
      return 'Buscar Técnicos';
    }
    if (profesion === Filtros.PathPersona.ARBITROS) {
      return 'Buscar Árbitros';
    }
    return 'Buscar Personas';
  };

  // Efecto para inicializar los filtros, vacío o con filtros del queryString
  useEffect(() => {
    if (searchParameters && searchParameters.size > 0) {
      // Hay parámetros en la URL -> cargar esos parámetros
      let parametros = searchParameters.entries ? [...searchParameters.entries()] : [];
      if (profesion) {
        parametros.push([FiltrosURL.PROFESION, profesion]);
      }
      let nuevoFiltro = Funciones.ArmarFiltroParametros(Filtros.PERSONAS, parametros);
      setFiltros({ type: Funciones.Reducer.NUEVO_FILTRO, value: nuevoFiltro });
    } else {
      // No hay parámetros en la URL -> inicializar
      setFiltros({ type: Funciones.Reducer.INICIALIZAR, value: Filtros.PERSONAS });
    }
  }, [searchParameters, profesion]);

  // Efecto separado para activar Apellido inicialmente (después de INICIALIZAR)
  useEffect(() => {
    if (
      filtros &&
      filtros.apellido &&
      !filtros.apellido.estado &&
      (!searchParameters || searchParameters.size === 0)
    ) {
      // Activar Apellido solo si no tiene parámetros de búsqueda
      setFiltros({ type: Funciones.Reducer.BOTON, value: Filtros.Persona.APELLIDO });
    }
  }, [filtros, searchParameters]);

  const buscarPersonas = useCallback(
    async (filtros) => {
      setFiltros({ type: Funciones.Reducer.BUSCAR, value: false });
      resetErroresSearch();
      const bodyFiltros = Funciones.ArmarBodyFiltros(Filtros.PERSONAS, filtros);
      if (bodyFiltros !== Funciones.ObjetoFiltrosVacio(Filtros.PERSONAS)) {
        props.onBuscando();
        searchData(filtros.path + '/filtros', bodyFiltros, props.onListaCargada);
      }
    },
    [props, resetErroresSearch, searchData]
  );

  // Efecto para ejecutar la búsqueda cuando se activa el flag buscar
  useEffect(() => {
    if (filtros && filtros.buscar) {
      buscarPersonas(filtros);
    }
  }, [filtros, buscarPersonas]);

  const onFiltroElegido = useCallback((filtroElegido) => {
    setFiltros({ type: Funciones.Reducer.BOTON, value: filtroElegido });
  }, []);

  const onFiltroChange = useCallback((filtro, valor) => {
    setFiltros({ type: filtro, value: valor });
  }, []);

  const onPaisElegido = useCallback((paisElegido) => {
    setFiltros({ type: Filtros.Persona.PAIS_NACIMIENTO, value: paisElegido });
  }, []);

  const onProvinciaElegida = useCallback((provinciaElegida) => {
    setFiltros({ type: Filtros.Persona.PROVINCIA_NACIMIENTO, value: provinciaElegida });
  }, []);

  const onLocalidadElegida = useCallback((localidadElegida) => {
    setFiltros({ type: Filtros.Persona.LOCALIDAD_NACIMIENTO, value: localidadElegida });
  }, []);

  const handleBuscar = useCallback(
    (event) => {
      event?.preventDefault?.();
      const error = Funciones.ValidarFiltros(Filtros.PERSONAS, filtros);
      if (error === '') {
        buscarPersonas(filtros);
      } else {
        alert(error);
      }
    },
    [filtros, buscarPersonas]
  );

  const handleLimpiar = () => {
    setFiltros({ type: Funciones.Reducer.INICIALIZAR, value: Filtros.PERSONAS });
  };

  // Configuración de campos para el componente genérico
  const campos = useMemo(
    () => [
      {
        id: Filtros.Persona.APELLIDO,
        nombre: 'Apellido',
        activo: filtros?.apellido?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Persona.APELLIDO}
            nombre={campo.nombre}
            selected={campo.activo}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <CampoEditableTexto
            nombre={Filtros.Persona.APELLIDO}
            editar={true}
            valor={filtros.apellido?.valor ?? ''}
            onChange={onFiltroChange}
            onPressEnter={handleBuscar}
          />
        ),
      },
      {
        id: Filtros.Persona.NOMBRE,
        nombre: 'Nombre',
        activo: filtros?.nombre?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Persona.NOMBRE}
            nombre={campo.nombre}
            selected={campo.activo}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <CampoEditableTexto
            nombre={Filtros.Persona.NOMBRE}
            editar={true}
            valor={filtros.nombre?.valor ?? ''}
            onChange={onFiltroChange}
            onPressEnter={handleBuscar}
          />
        ),
      },
      {
        id: Filtros.Persona.PAIS_NACIMIENTO,
        nombre: 'País',
        activo: filtros?.paisNacimiento?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Persona.PAIS_NACIMIENTO}
            nombre={campo.nombre}
            selected={campo.activo}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectPais paisActual={filtros.paisNacimiento?.valor} onPaisElegido={onPaisElegido} />
        ),
      },
      {
        id: Filtros.Persona.PROVINCIA_NACIMIENTO,
        nombre: 'Provincia',
        activo: filtros?.provinciaNacimiento?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Persona.PROVINCIA_NACIMIENTO}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={filtros?.paisNacimiento?.estado && filtros?.paisNacimiento?.valor?.id}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectProvincia
            pais={
              filtros?.paisNacimiento?.estado && filtros?.paisNacimiento?.valor?.id
                ? filtros.paisNacimiento.valor
                : null
            }
            provinciaActual={filtros.provinciaNacimiento?.valor}
            onProvinciaElegida={onProvinciaElegida}
          />
        ),
      },
      {
        id: Filtros.Persona.LOCALIDAD_NACIMIENTO,
        nombre: 'Localidad',
        activo: filtros?.localidadNacimiento?.estado ?? false,
        renderToggle: (campo) => (
          <ItemLista
            id={Filtros.Persona.LOCALIDAD_NACIMIENTO}
            nombre={campo.nombre}
            selected={campo.activo}
            enabled={filtros?.paisNacimiento?.estado && filtros?.paisNacimiento?.valor?.id}
            onItemElegido={onFiltroElegido}
          />
        ),
        renderCampo: (campo, filtros) => (
          <SelectLocalidad
            pais={
              filtros?.paisNacimiento?.estado && filtros?.paisNacimiento?.valor?.id
                ? filtros.paisNacimiento.valor
                : null
            }
            provincia={
              filtros?.provinciaNacimiento?.estado && filtros?.provinciaNacimiento?.valor?.id
                ? filtros.provinciaNacimiento.valor
                : null
            }
            localidadActual={filtros.localidadNacimiento?.valor}
            onLocalidadElegida={onLocalidadElegida}
          />
        ),
      },
    ],
    [
      filtros,
      onFiltroElegido,
      onFiltroChange,
      onPaisElegido,
      onProvinciaElegida,
      onLocalidadElegida,
      handleBuscar,
    ]
  );

  if (!filtros) {
    return null;
  }

  return (
    <FiltrosGenerico
      filtros={filtros}
      campos={campos}
      onFiltroElegido={onFiltroElegido}
      onBuscar={handleBuscar}
      onLimpiar={handleLimpiar}
      isBuscando={isBuscando}
      errores={erroresSearch}
      titulo={getTituloFiltro()}
      collapsibleEnMobile={true}
      initialCollapsed={props.initialCollapsed ?? false}
      hasResults={props.hasResults ?? false}
    />
  );
};

export default FiltrosPersonas;
